import {
  AgreementType,
  ApplicationProductTypeEnum,
  EmploymentType,
  LineOfCreditSubmitApplicationError,
  LineOfCreditSubmitApplicationSuccess,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import LineOfCreditErrorScreenComponent, {
  VlocStepName,
} from 'features/lineOfCredit/screens/error/LineOfCreditErrorScreenComponent';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import EmploymentInfoForm from 'shared/components/EmploymentInfoForm/EmploymentInfoForm';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useAgreements from 'shared/hooks/useAgreements/useAgreements';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { getUuid } from 'shared/utils/getUuid/getUuid';

import useFetchEmployeeStatus from './hooks/useFetchEmployeeStatus';
import useGetVLocApplicationStatus from './hooks/useGetVLocApplicationStatus';
import useHardCheckPollingEffect from './hooks/useHardCheckPollingEffect';
import useLineOfCreditSubmitApplication from './hooks/useLineOfCreditSubmitApplication';

const ApplyEmploymentScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('vloc', {
    keyPrefix: 'applyEmployment',
  });

  const customAttributes = { credit_check_type: 'hard' };

  const { registrationInfoId, tierId } = useLocalSearchParams<{
    registrationInfoId: string;
    tierId: string;
  }>();

  const { error, fetching, result } = useFetchEmployeeStatus();
  const employmentTypes = result as EmploymentType[] | undefined;

  const {
    agreementsListDetails: agreementsData,
    errorModel: agreementsError,
    loading: agreementsFetching,
  } = useAgreements([ApplicationProductTypeEnum.LineOfCredit]);

  const vlocAgreementPdfUrl =
    agreementsData?.find(
      (agreement: { type: AgreementType }) =>
        agreement.type === AgreementType.LineOfCreditAgreement,
    )?.resource?.pdfDocument?.url ?? '';

  const vlocAgreementId = agreementsData?.find(
    (agreement: { type: AgreementType }) =>
      agreement.type === AgreementType.LineOfCreditAgreement,
  )?.id;

  const {
    result: hardCreditCheckResult,
    error: hardCreditCheckError,
    fetching: hardCreditCheckLoading,
    triggerMutation,
  } = useLineOfCreditSubmitApplication();
  const [applicationId, setApplicationId] = useState<string>('');

  const { result: hardCheckResult, refetchCheck } =
    useGetVLocApplicationStatus(applicationId);

  const { isChecking, attempts, showError, setIsChecking, setShowError } =
    useHardCheckPollingEffect(hardCheckResult, refetchCheck, tierId);

  useEffect(() => {
    const hardCheckResultSuccess =
      hardCreditCheckResult as LineOfCreditSubmitApplicationSuccess;
    const hardCheckResultError =
      hardCreditCheckResult as LineOfCreditSubmitApplicationError;
    if (hardCheckResultSuccess && hardCheckResultSuccess.applicationId) {
      setApplicationId(hardCheckResultSuccess.applicationId);
      setIsChecking(true);
    } else if (hardCheckResultError) {
      setShowError(true);
    }
  }, [hardCreditCheckResult, setIsChecking, setShowError]);

  const isLoading = fetching || agreementsFetching || hardCreditCheckLoading;
  const hasError = !!(
    error ||
    agreementsError ||
    hardCreditCheckError ||
    showError
  );
  const hasData = !!employmentTypes && !!vlocAgreementPdfUrl.length;
  const showButtons = hasData && !isLoading && !isChecking && !hasError;

  const handleSubmit = (formValues: {
    employmentStatus: string;
    monthlyIncome: string;
  }) => {
    const employmentType = employmentTypes?.find(
      (type) => type.name === formValues.employmentStatus,
    );
    const monthlyIncomeValue = formValues.monthlyIncome.replace(/\D/g, ''); // Remove non-numeric characters from monthlyIncome to pass it as pure number

    if (!employmentType) {
      return null;
    }

    const submitApplicationInput = {
      agreements: vlocAgreementId ? [vlocAgreementId] : [],
      employmentTypeId: employmentType?.id,
      monthlyIncome: {
        amount: monthlyIncomeValue,
        currency: 'USD',
      },
      registrationInfoId: registrationInfoId,
      tierId: tierId,
    };

    triggerMutation(getUuid(), submitApplicationInput);
  };

  const dismissToTab = useDismissToRootTab();

  const goBack = () => {
    router.back();
  };

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  const getBackHandler = () => {
    if (isChecking) {
      return () => null;
    }

    return hasError ? goHome : goBack;
  };

  useBackHandler(getBackHandler());

  useViewDidFocusScreenAnalyticsEvent(
    isChecking
      ? {
          key: 'vlocCreditCheck',
          customAttributes: customAttributes,
        }
      : null,
  );

  return (
    <View style={styles.mainContainer}>
      <Stack.Screen
        options={{
          title: '',
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
          headerShown: !isChecking && !hasError,
          gestureEnabled: !isChecking && !hasError,
          headerBackVisible: !isChecking && !hasError,
        }}
      />
      {isChecking ? (
        <View style={styles.loaderStyle}>
          <FullScreenLoading
            isCoinLoader
            titleText={translatedText('crunchingTheNumbers.title', {
              keyPrefix: '',
            })}
            bodyText={translatedText('crunchingTheNumbers.subTitle', {
              keyPrefix: '',
            })}
          />
        </View>
      ) : null}
      {!isChecking && attempts === 0 ? (
        <View style={styles.errorContainer}>
          <ScreenTransitionOverlay
            isLoading={isLoading}
            LoadingComponent={<LoadingSpinner />}
            ErrorComponent={
              hasError ? (
                <LineOfCreditErrorScreenComponent
                  screenAnalyticsId="/varo-line-of-credit/line-of-credit-entry-point/LineOfCreditErrorScreenComponent-applyEmploymentScreen"
                  trackingAnalyticsId="apply-employment-error-tracking"
                  trackingCustomAttributes={{
                    step_name:
                      hardCreditCheckError || showError
                        ? VlocStepName.HARD_CREDIT_CHECK
                        : VlocStepName.VLOC_GENERAL,
                  }}
                  insetBottomSafeArea={true}
                />
              ) : null
            }
          />
          {hasData ? (
            <View style={styles.subContainer}>
              <EmploymentInfoForm
                employmentTypes={employmentTypes}
                isVloc={true}
                pdfType={ApplicationProductTypeEnum.LineOfCredit}
                handleSubmit={handleSubmit}
                showButtons={showButtons}
                fetchingStatus={hardCreditCheckLoading}
              />
            </View>
          ) : null}
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  errorContainer: {
    flex: 1,
  },
  loaderStyle: {
    flex: 1,
  },
  mainContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  subContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingHorizontal: theme.spacing.m,
  },
});

export default ApplyEmploymentScreen;
